<template>
  <div class="q-select-organisation detail-page-padding">
    <div class="head">
        <div class="left">
            <arrow-button color="black" direction="left" @click="back"></arrow-button>
            <h2>{{ project.name }}</h2>
            <q-tag class="status" size="small" variation="warning">{{ $t(`projectStatus.${project.status}`) }}</q-tag>
        </div>
      </div>
      <div class="search">
          <div class="panel">
            <div class="title">
                <div>
                    <span class="label">Stap 1</span>
                </div>
                <h3>{{title}}</h3>
            </div>
            <div class="input">
                <div class="input-label">
                    Opdrachtnemer
                </div>
                <q-input labelPosition="inside"
                    v-model="input"
                    placeholder="Zoek op naam, adres of KvK-nummer"
                    @input="_search"
                    autofocus
                />
            </div>
          </div>
      </div>
      <div class="results" v-if="input !== ''">
          <q-table :columns="getColumns" :data="branches" width="800px" :loading="loading" :loadingRows="5">
              <template v-slot:row="{ column, row }">
                  <div v-if="column === 'action'">
                    <q-button class="edit" 
                        variation="ghost" 
                        size="small" 
                        @click="select(row)">Kies organisatie</q-button>
                  </div>
                  <div v-else>
                      {{row[column]}}
                  </div>
              </template>
          </q-table>
          
          <!-- Show More Results Button -->
          <show-more-button 
              :show="branches.length > 0 && hasMoreResults"
              :loading="loadingMore"
              @click="showMoreResults"
          />
      </div>
  </div>
</template>

<script>

import _ from "lodash";

import { KVK_COMPANIES, GET_ORGANISATION_BRANCHES } from '../../../graphql/queries'

import ArrowButton from '@/components/ArrowButton';
import ShowMoreButton from './ShowMoreButton';

export default {
    name: 'q-select-organisation',
    components: {
        'arrow-button': ArrowButton,
        'show-more-button': ShowMoreButton
    },
    props: {
        project: {
            type: Object,
            required: true
        },
        /**
         * Type of the organisation select:
         * main: Only queries main organisations
         * branch: Only queries branch organisations for the given organisationId in 'Opdrachtnemer'
         */
        type: {
            type: String,
            required: true
        },
        title: {
            type: String,
            default: 'Kies een organisatie'
        }
    },
    data() {
        return {
            input: '',
            page: 1,
            resultsPerPage: 100,
            hasMoreResults: false,
            loadingMore: false,
            mainColumns: [
                {
                    field: 'name',
                    label: 'Organisatie',
                    width: '260px',
                },
                {
                    field: 'address',
                    label: 'Adres',
                    width: '200px',
                },
                {
                    field: 'code',
                    label: 'Postcode'
                },
                {
                    field: 'city',
                    label: 'Plaats'
                },
                {
                    field: 'action',
                    label: '',
                    width: '120px',
                    align: 'right'
                }
            ],
            branchColumns: [
                {
                    field: 'name',
                    label: 'Organisatie',
                    width: '260px',
                },
                {
                    field: 'address',
                    label: 'Adres',
                    width: '200px',
                },
                {
                    field: 'city',
                    label: 'Plaats'
                },
                {
                    field: 'action',
                    label: '',
                    width: '120px',
                    align: 'right'
                }
            ],
            loading: false,
            branches: []
        }
    },
    methods: {
        _search: _.debounce(function() {
            return this.search();
        }, 500),
        search() {
            switch (this.type) {
                case 'main':
                    this.getMainOrganisations(true);
                    break;
                case 'contractor-branch':
                    this.getBranchOrganisations('contractor-branch');
                    break;
                default:
                    console.error(`type '${this.type}' not found`)
                    break;
            }
        },
        select(row) {
            this.$emit('input', row.id);
        },
        setMainBranches(data) {
            const items = data.kvk_companies;
            this.branches = this.formatMainBranches(items);
        },
        async getMainOrganisations(resetPagination = true) {
            if (resetPagination) {
                this.page = 1;
                this.branches = [];
            }
            
            this.loading = true;
            
            try {
                const response = await this.$apollo.query({
                    query: KVK_COMPANIES,
                    variables: {
                        term: this.input,
                        page: this.page
                    },
                    fetchPolicy: 'no-cache'
                });
                
                this.setMainBranches(response.data);
                this.hasMoreResults = response.data.kvk_companies && response.data.kvk_companies.length === this.resultsPerPage;
                this.loading = false;
                this.loadingMore = false;
            } catch (err) {
                console.error(err);
                this.loading = false;
                this.loadingMore = false;
            }
        },
        async showMoreResults() {
            try {
                this.loadingMore = true;
                this.page += 1;
                
                const response = await this.$apollo.query({
                    query: KVK_COMPANIES,
                    variables: {
                        term: this.input,
                        page: this.page
                    },
                    fetchPolicy: 'no-cache'
                });
                
                const newBranches = this.formatMainBranches(response.data.kvk_companies);
                this.branches = [...this.branches, ...newBranches];
                
                this.hasMoreResults = response.data.kvk_companies && response.data.kvk_companies.length === this.resultsPerPage;
                this.loadingMore = false;
            } catch (err) {
                console.error(err);
                this.loadingMore = false;
            }
        },
        formatMainBranches(items) {
            const mainBranches = items.filter(item => item.isMainBranch == true);
            return mainBranches.map(branch => {
                return {
                    name: branch.tradeNames.businessName,
                    address: `${branch.addresses[0].street} ${branch.addresses[0].houseNumber}`,
                    code: branch.addresses[0].postalCode,
                    city: branch.addresses[0].city,
                    id: branch.mainExists.id
                };
            });
        },
        setBranchOrganisations(organisation) {
            this.branches = organisation.childs.map(branch => {
                return {
                    id: branch.id,
                    name: branch.name,
                    address: `${branch.address.line1} ${branch.address.line2}`,
                    city: branch.address.city
                }
            })
        },
        getBranchOrganisations(masterId) {
            const contractorId = masterId === 'contractor-branch' ? this.project.contractorId : null;
            const clientId = masterId === 'client-branch' ? this.project.clientId : null;
    
            if (!clientId && !contractorId) return console.error('no contractor or client id set')

            const organisationId = contractorId ? contractorId : clientId
            
            this.$apollo
                .query({
                    query: GET_ORGANISATION_BRANCHES,
                    variables: { organisationId },
                })
                .then(response => {
                    this.setBranchOrganisations(response.data.organisation);
                })
                .catch(err => {
                    console.log(err);
                });
        },
        back() {
            this.$parent.cancel();
        }
    },
    computed: {
        getColumns: function() {
            switch (this.type) {
                case 'main':
                    return this.mainColumns
                    break;
                case 'contractor-branch':
                    return this.branchColumns
                    break;
                default:
                    break;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../assets/style/_variables.scss";
@import "../assets/style/fonts/fonts.css";


.head {
    margin-bottom: 48px;
    display: flex;
    justify-content: space-between;

    .left {
        display: flex;
        align-items: center;

        .status {
            margin-left: 10px;
        }

        h2 {
            font-weight: 500;
            font-size: 23px;
            line-height: 23px;
        }
    }
}

.results {
    display: flex;
    justify-content: center;
    margin-top: 50px;

    & .edit {
        justify-content: right !important;
    }
}

.input-label {
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;

    margin-top: 15px;
}

.search {
    display: flex;
    justify-content: center;

    margin-top: 150px;

    .panel {
        width: 460px;

        .title {
            display: flex;
            align-items: center;
            margin-left: -64px;

            h3 {
                margin-left: 24px;
            }
        }
    }
}

.label {
    font-family: Gotham;
    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    line-height: 16px;
    background: #F2F2F2;
    border: 1px solid #DEE2E6;
    border-radius: 4px;
    color: $color-grey-7;
    padding: 4px;
}



.q-select-organisation {

    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 1px;

    z-index: 100;
    background-color: white;
}

h3 {
    font-weight: 500;
    font-size: 19px;
    line-height: 24px;
}

</style>